import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import Input from '../../Core/Input/Input'
import InputLabel from '../../Core/InputLabel'
import Textarea from '../../Core/Textarea/Textarea'
import AppCheckbox from '../../Core/AppCheckbox'
import Button from '../../Core/Button/Button'

import { createCustomer, updateCustomer } from '../../../store/customers/actions'
import { showSnackbar } from '../../../store/settings/actions'

import styles from './styles.module.scss'

const REQUIRED_BORDER_COLOR = '#de4343'

const CustomerForm = ({ initialFormData = null, isCreate = true, companyId, customerId, onSubmit = null, onCancel = null }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  const defaultFormData = {
    // Company information
    legal_name: '',
    display_name: '',
    company_registration_number: '',
    vat_number: '',
    website: '',
    // Billing address
    billing_address_line_1: '',
    billing_address_line_2: '',
    billing_city: '',
    billing_postal_code: '',
    billing_country: '',
    // Billing contact
    billing_email: '',
    phone: '',
    // Contact person
    contact_person_name: '',
    contact_person_surname: '',
    contact_person_email: '',
    contact_person_phone: '',
    // Invoice details
    default_currency: '',
    default_payment_terms_days: '',
    preferred_invoice_language: '',
    // Tax & Status
    is_vat_registered: false,
    reverse_charge_applicable: false,
    is_active: true,
    // Notes
    notes: '',
  }

  const [formData, setFormData] = useState(initialFormData || defaultFormData)
  const [requiredErrors, setRequiredErrors] = useState({
    legal_name: false,
    display_name: false,
    billing_email: false,
  })

  useEffect(() => {
    if (initialFormData) {
      setFormData((prev) => ({ ...prev, ...initialFormData }))
    }
  }, [initialFormData])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (name in requiredErrors) {
      setRequiredErrors(prev => ({
        ...prev,
        [name]: value.trim() === '',
      }))
    }
  }

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const nextRequiredErrors = {
      legal_name: !formData.legal_name?.trim(),
      display_name: !formData.display_name?.trim(),
      billing_email: !formData.billing_email?.trim(),
    }

    setRequiredErrors(nextRequiredErrors)

    if (Object.values(nextRequiredErrors).some(Boolean)) {
      return
    }
    
    if (onSubmit) {
      onSubmit(formData, isCreate)
    } else {
      if (isCreate) {
        dispatch(createCustomer(companyId, formData))
          .then(() => {
            dispatch(showSnackbar(t('Customer created successfully'), 'success'))
            history.push(`/${companyId}/settings/categories`)
          })
          .catch(() => {
            dispatch(showSnackbar(t('Failed to save customer'), 'error'))
          })
      } else {
        dispatch(updateCustomer(companyId, customerId, formData))
          .then(() => {
            dispatch(showSnackbar(t('Customer updated successfully'), 'success'))
            history.push(`/${companyId}/settings/categories`)
          })
          .catch(() => {
            dispatch(showSnackbar(t('Failed to save customer'), 'error'))
          })
      }
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      history.push(`/${companyId}/settings/categories`)
      // history.push(`/${companyId}/settings/categories/customers/create`);
    }
  }

  const hasFieldErrors = Object.values(requiredErrors).some(Boolean)

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit} noValidate>
        {/* Company Information Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('Company information')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel
                text={(
                  <>
                    {t('Legal name')} <span className={styles.requiredAsterisk}>*</span>
                  </>
                )}
                htmlFor='legal_name'
                className={styles.label}
              />
              <Input
                id='legal_name'
                name='legal_name'
                value={formData.legal_name}
                onChange={handleInputChange}
                placeholder={t('Enter legal name')}
                style={requiredErrors.legal_name ? { borderColor: REQUIRED_BORDER_COLOR } : undefined}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel
                text={(
                  <>
                    {t('Display name')} <span className={styles.requiredAsterisk}>*</span>
                  </>
                )}
                htmlFor='display_name'
                className={styles.label}
              />
              <Input
                id='display_name'
                name='display_name'
                value={formData.display_name}
                onChange={handleInputChange}
                placeholder={t('Enter display name')}
                style={requiredErrors.display_name ? { borderColor: REQUIRED_BORDER_COLOR } : undefined}
                fullWidth
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Company registration number')} htmlFor='company_registration_number' className={styles.label} />
              <Input
                id='company_registration_number'
                name='company_registration_number'
                value={formData.company_registration_number}
                onChange={handleInputChange}
                placeholder={t('Enter registration number')}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('VAT number')} htmlFor='vat_number' className={styles.label} />
              <Input
                id='vat_number'
                name='vat_number'
                value={formData.vat_number}
                onChange={handleInputChange}
                placeholder={t('Enter VAT number')}
                fullWidth
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Website')} htmlFor='website' className={styles.label} />
              <Input
                id='website'
                name='website'
                type='url'
                value={formData.website}
                onChange={handleInputChange}
                placeholder={t('Enter website URL')}
                fullWidth
              />
            </div>
          </div>
        </div>

        {/* Billing Address Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('Billing address')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Address line 1')} htmlFor='billing_address_line_1' className={styles.label} />
              <Input
                id='billing_address_line_1'
                name='billing_address_line_1'
                value={formData.billing_address_line_1}
                onChange={handleInputChange}
                placeholder={t('Enter address line 1')}
                fullWidth
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Address line 2')} htmlFor='billing_address_line_2' className={styles.label} />
              <Input
                id='billing_address_line_2'
                name='billing_address_line_2'
                value={formData.billing_address_line_2}
                onChange={handleInputChange}
                placeholder={t('Enter address line 2')}
                fullWidth
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('City')} htmlFor='billing_city' className={styles.label} />
              <Input
                id='billing_city'
                name='billing_city'
                value={formData.billing_city}
                onChange={handleInputChange}
                placeholder={t('Enter city')}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('Postal code')} htmlFor='billing_postal_code' className={styles.label} />
              <Input
                id='billing_postal_code'
                name='billing_postal_code'
                value={formData.billing_postal_code}
                onChange={handleInputChange}
                placeholder={t('Enter postal code')}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('Country')} htmlFor='billing_country' className={styles.label} />
              <Input
                id='billing_country'
                name='billing_country'
                value={formData.billing_country}
                onChange={handleInputChange}
                placeholder={t('Enter country')}
                fullWidth
              />
            </div>
          </div>
        </div>

        {/* Billing Contact Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('Billing Contact')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel
                text={(
                  <>
                    {t('Email')} <span className={styles.requiredAsterisk}>*</span>
                  </>
                )}
                htmlFor='billing_email'
                className={styles.label}
              />
              <Input
                id='billing_email'
                name='billing_email'
                type='email'
                value={formData.billing_email}
                onChange={handleInputChange}
                placeholder={t('Enter email')}
                style={requiredErrors.billing_email ? { borderColor: REQUIRED_BORDER_COLOR } : undefined}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('Phone')} htmlFor='phone' className={styles.label} />
              <Input
                id='phone'
                name='phone'
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t('Enter phone number')}
                fullWidth
              />
            </div>
          </div>
        </div>

        {/* Contact Person Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('Contact person')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Name')} htmlFor='contact_person_name' className={styles.label} />
              <Input
                id='contact_person_name'
                name='contact_person_name'
                value={formData.contact_person_name}
                onChange={handleInputChange}
                placeholder={t('Enter name')}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('Surname')} htmlFor='contact_person_surname' className={styles.label} />
              <Input
                id='contact_person_surname'
                name='contact_person_surname'
                value={formData.contact_person_surname}
                onChange={handleInputChange}
                placeholder={t('Enter surname')}
                fullWidth
              />
            </div>
          </div>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Email')} htmlFor='contact_person_email' className={styles.label} />
              <Input
                id='contact_person_email'
                name='contact_person_email'
                type='email'
                value={formData.contact_person_email}
                onChange={handleInputChange}
                placeholder={t('Enter email')}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('Phone')} htmlFor='contact_person_phone' className={styles.label} />
              <Input
                id='contact_person_phone'
                name='contact_person_phone'
                value={formData.contact_person_phone}
                onChange={handleInputChange}
                placeholder={t('Enter phone number')}
                fullWidth
              />
            </div>
          </div>
        </div>

        {/* Invoice Details Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('Invoice details')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Default currency')} htmlFor='default_currency' className={styles.label} />
              <Input
                id='default_currency'
                name='default_currency'
                value={formData.default_currency}
                onChange={handleInputChange}
                placeholder={t('Enter currency code (e.g., USD, EUR)')}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('Default payment terms (days)')} htmlFor='default_payment_terms_days' className={styles.label} />
              <Input
                id='default_payment_terms_days'
                name='default_payment_terms_days'
                type='number'
                value={formData.default_payment_terms_days}
                onChange={handleInputChange}
                placeholder={t('Enter number of days')}
                fullWidth
              />
            </div>
            <div className={styles.formField}>
              <InputLabel text={t('Preferred invoice language')} htmlFor='preferred_invoice_language' className={styles.label} />
              <Input
                id='preferred_invoice_language'
                name='preferred_invoice_language'
                value={formData.preferred_invoice_language}
                onChange={handleInputChange}
                placeholder={t('Enter language code (e.g., en, lt)')}
                fullWidth
              />
            </div>
          </div>
        </div>

        {/* Tax & Status Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('Tax & Status')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.checkboxField}>
              <div className={styles.checkboxContainer}>
                <AppCheckbox
                  checked={formData.is_vat_registered}
                  onChange={() => handleCheckboxChange('is_vat_registered')}
                />
                <span className={styles.checkboxLabel}>{t('VAT registered')}</span>
              </div>
            </div>
            <div className={styles.checkboxField}>
              <div className={styles.checkboxContainer}>
                <AppCheckbox
                  checked={formData.reverse_charge_applicable}
                  onChange={() => handleCheckboxChange('reverse_charge_applicable')}
                />
                <span className={styles.checkboxLabel}>{t('Reverse charge applicable')}</span>
              </div>
            </div>
            <div className={styles.checkboxField}>
              <div className={styles.checkboxContainer}>
                <AppCheckbox
                  checked={formData.is_active}
                  onChange={() => handleCheckboxChange('is_active')}
                />
                <span className={styles.checkboxLabel}>{t('Active')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('Notes')}</h3>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <InputLabel text={t('Notes')} htmlFor='notes' />
              <Textarea
                id='notes'
                name='notes'
                value={formData.notes}
                className={styles.textarea}
                onChange={handleInputChange}
                placeholder={t('Enter any additional notes')}
                label=''
              />
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          <Button
            type='submit'
            disabled={hasFieldErrors}
            fillWidth
            size='big'
          >
            {t('Save & Close')}
          </Button>
          <Button
            onClick={handleCancel}
            white
            fillWidth
            size='big'
          >
            {t('Cancel')}
          </Button>
          
        </div>
      </form>
    </div>
  )
}

export default CustomerForm
